import { useState, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mediaService, MediaUpload, StorageWarning } from '@/services/media.service';
import { MediaItem } from '@/types/posts';
import { getFileType, createPreviewUrl } from '@/utils/mediaValidation';
import { generatePDFThumbnail, isPDF } from '@/utils/pdfThumbnail';
import { devLog } from '@/utils/logger';
import { recordSlowOp } from '@/utils/perfTelemetry';
import { formatApiError } from '@/services/api';

/**
 * React Query hooks for media upload with direct S3 upload
 */

export interface UploadState {
  id: string;
  filename: string;
  size: number;
  type: 'image' | 'video' | 'pdf';
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
  media?: MediaItem;
  localPreview?: string;
  uploadData?: MediaUpload; // Store backend MediaUpload data
  /** Non-blocking storage-quota nudge (warn mode only) — see media.service.ts. */
  storageWarning?: StorageWarning;
}

/**
 * Hook for managing multiple file uploads with direct S3 upload
 */
export const useMediaUpload = () => {
  const [uploads, setUploads] = useState<Map<string, UploadState>>(new Map());

  /**
   * Upload a single file using direct upload
   * Returns both the upload ID and the MediaItem for immediate use
   */
  const uploadFile = useCallback(async (
    file: File
  ): Promise<{ id: string; media: MediaItem; storageWarning?: StorageWarning }> => {
    // Generate temporary ID for tracking
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(7)}`;

    // Generate preview (PDF thumbnail or blob URL)
    let localPreview = '';
    try {
      if (isPDF(file)) {
        // Generate thumbnail from first page
        const t0 = performance.now();
        localPreview = await generatePDFThumbnail(file, {
          maxWidth: 400,
          maxHeight: 400,
          quality: 0.85,
        });
        recordSlowOp('pdfThumbnail', performance.now() - t0);
      } else {
        // Use blob URL for images/videos
        localPreview = createPreviewUrl(file);
      }
    } catch (error) {
      devLog.warn('[MediaUpload] Failed to generate preview:', error);
      // Fallback to empty preview (will show icon)
      localPreview = '';
    }

    // Create initial upload state
    const uploadState: UploadState = {
      id: tempId,
      filename: file.name,
      size: file.size,
      type: getFileType(file),
      progress: 0,
      status: 'pending',
      localPreview,
    };

    setUploads((prev) => new Map(prev).set(tempId, uploadState));

    // VIDEO: browser -> S3 direct upload via presigned URL (cdb #304).
    // Routing video through our server (the old directUpload path) held one
    // request open for the whole transfer with a hard 300s timeout — large
    // videos exceeded it, axios threw, and the UI discarded the upload even
    // though the server had already finished. IMAGE/PDF stay on directUpload
    // below because they need the server-side optimize/thumbnail step.
    const isVideo = file.type.startsWith('video/');

    try {
      if (isVideo) {
        // Update to uploading (real progress arrives via uploadToS3's onProgress below)
        setUploads((prev) => {
          const next = new Map(prev);
          const state = next.get(tempId);
          if (state) {
            next.set(tempId, { ...state, status: 'uploading', progress: 0 });
          }
          return next;
        });

        // Step 1: ask the backend for a presigned S3 PUT URL (tiny JSON call)
        const { uploadId, presignedUrl } = await mediaService.getPresignedUrl(
          file.name,
          file.type,
          file.size
        );

        // Step 2: browser -> S3 direct PUT, no server byte-hop, no 300s cap.
        await mediaService.uploadToS3(presignedUrl, file, (progress) => {
          setUploads((prev) => {
            const next = new Map(prev);
            const state = next.get(tempId);
            if (state) {
              next.set(tempId, { ...state, progress });
            }
            return next;
          });
        });

        // Step 3: tell the backend the upload finished — this flips the
        // MediaUpload row to COMPLETED and enqueues async transcoding
        // (backend then moves it to PROCESSING, and eventually READY/FAILED).
        // This is a small JSON call, not the big transfer, so it can't hit
        // the same timeout that caused the original bug.
        await mediaService.completeUpload(uploadId);

        // The backend doesn't return url/thumbnail synchronously for videos
        // (they're not known until the async pipeline finishes) — filename
        // and size are already known client-side from the File object.
        // `id` MUST be the backend uploadId: MediaSection reads `item.id` as
        // the uploadId it hands to useVideoProcessingPoll.
        const media: MediaItem = {
          id: uploadId,
          url: '',
          type: 'video',
          thumbnail: undefined,
          filename: file.name,
          size: file.size,
          isProcessing: true,
        };

        const uploadData: MediaUpload = {
          id: uploadId,
          fileName: file.name,
          originalUrl: '',
          optimizedUrl: '',
          thumbnailUrl: null,
          mediaType: 'VIDEO',
          fileSize: file.size,
          status: 'PROCESSING',
          createdAt: new Date().toISOString(),
        };

        setUploads((prev) => {
          const next = new Map(prev);
          next.delete(tempId);
          next.set(uploadId, {
            id: uploadId,
            filename: file.name,
            size: file.size,
            type: 'video',
            progress: 100,
            status: 'completed',
            media,
            uploadData,
            localPreview: uploadState.localPreview,
          });
          return next;
        });

        return { id: uploadId, media };
      }

      // IMAGE / PDF: unchanged — these need the server-side optimize/thumbnail
      // step, so they stay on the multipart directUpload path.
      // Update to uploading
      setUploads((prev) => {
        const next = new Map(prev);
        const state = next.get(tempId);
        if (state) {
          next.set(tempId, { ...state, status: 'uploading', progress: 50 });
        }
        return next;
      });

      // Upload directly to S3 (server-side, then re-uploaded to S3 by the backend)
      const uploadData = await mediaService.directUpload(file);

      // Create MediaItem
      // For videos, thumbnailUrl is generated asynchronously — never fall back to
      // optimizedUrl (the MP4 itself) as a thumbnail; that breaks <img>.
      // undefined thumbnail causes the placeholder icon branch in MediaSection.
      const resolvedThumbnail: string | undefined =
        uploadData.mediaType === 'VIDEO'
          ? (uploadData.thumbnailUrl ?? undefined)
          : (uploadData.thumbnailUrl || uploadData.optimizedUrl || undefined);

      const media: MediaItem = {
        id: uploadData.id,
        url: uploadData.optimizedUrl || uploadData.originalUrl,
        type: uploadData.mediaType === 'VIDEO' ? 'video' : uploadData.mediaType === 'PDF' ? 'pdf' : 'image',
        thumbnail: resolvedThumbnail,
        filename: uploadData.fileName,
        size: uploadData.fileSize,
        width: uploadData.width,
        height: uploadData.height,
        // Signal that async processing is in-flight when thumbnailUrl is absent
        isProcessing: uploadData.mediaType === 'VIDEO' && !uploadData.thumbnailUrl,
        // Carry GIF mime type so downstream checks can detect animated GIFs
        // without relying on mediaType (which maps to 'image' in the MediaItem type).
        mimeType: uploadData.mediaType === 'GIF' ? 'image/gif' : undefined,
      };

      // Update state with completed upload
      setUploads((prev) => {
        const next = new Map(prev);
        // Remove temp entry and add real one with backend ID
        next.delete(tempId);
        next.set(uploadData.id, {
          id: uploadData.id,
          filename: uploadData.fileName,
          size: uploadData.fileSize,
          type: uploadData.mediaType === 'VIDEO' ? 'video' : uploadData.mediaType === 'PDF' ? 'pdf' : 'image',
          progress: 100,
          status: 'completed',
          media,
          uploadData,
          localPreview: uploadState.localPreview,
          storageWarning: uploadData.storageWarning,
        });
        return next;
      });

      // Return both ID and MediaItem for immediate use (avoids state timing issues)
      return { id: uploadData.id, media, storageWarning: uploadData.storageWarning };
    } catch (error) {
      const errorMessage = formatApiError(error).message;
      setUploads((prev) => {
        const next = new Map(prev);
        const state = next.get(tempId);
        if (state) {
          next.set(tempId, {
            ...state,
            status: 'error',
            error: errorMessage,
          });
        }
        return next;
      });
      throw error;
    }
  }, []);

  /**
   * Remove an upload from the list
   */
  const removeUpload = useCallback((uploadId: string) => {
    setUploads((prev) => {
      const next = new Map(prev);
      const state = next.get(uploadId);

      // Revoke blob URL to free memory
      if (state?.localPreview) {
        URL.revokeObjectURL(state.localPreview);
      }

      next.delete(uploadId);
      return next;
    });
  }, []);

  /**
   * Get completed media items
   */
  const getCompletedMedia = useCallback((): MediaItem[] => {
    return Array.from(uploads.values())
      .filter((upload) => upload.status === 'completed' && upload.media)
      .map((upload) => upload.media!);
  }, [uploads]);

  /**
   * Get upload IDs for attaching to posts
   */
  const getUploadIds = useCallback((): string[] => {
    return Array.from(uploads.values())
      .filter((upload) => upload.status === 'completed' && upload.uploadData)
      .map((upload) => upload.uploadData!.id);
  }, [uploads]);

  /**
   * Reset all uploads
   */
  const reset = useCallback(() => {
    // Revoke all blob URLs
    uploads.forEach((upload) => {
      if (upload.localPreview) {
        URL.revokeObjectURL(upload.localPreview);
      }
    });

    setUploads(new Map());
  }, [uploads]);

  return {
    uploadFile,
    removeUpload,
    getCompletedMedia,
    getUploadIds,
    reset,
    uploads: Array.from(uploads.values()),
    uploadsMap: uploads,
  };
};

/**
 * Legacy hook for single file upload (backward compatibility)
 */
export const useUploadMedia = () => {
  const [uploadProgress, setUploadProgress] = useState<{
    uploadId: string;
    progress: number;
    status: 'uploading' | 'processing' | 'completed' | 'failed';
    error?: string;
  } | null>(null);

  const mutation = useMutation({
    mutationFn: async (file: File): Promise<MediaItem> => {
      try {
        // Step 1: Get presigned URL
        const { uploadId, presignedUrl } = await mediaService.getPresignedUrl(
          file.name,
          file.type,
          file.size
        );

        setUploadProgress({
          uploadId,
          progress: 0,
          status: 'uploading',
        });

        // Step 2: Upload to S3
        await mediaService.uploadToS3(presignedUrl, file, (progress) => {
          setUploadProgress((prev) =>
            prev ? { ...prev, progress } : null
          );
        });

        setUploadProgress((prev) =>
          prev ? { ...prev, progress: 100, status: 'processing' } : null
        );

        // Step 3: Complete upload
        const uploadStatus = await mediaService.completeUpload(uploadId);

        if (uploadStatus.status === 'failed') {
          throw new Error(uploadStatus.error || 'Upload failed');
        }

        setUploadProgress((prev) =>
          prev ? { ...prev, status: 'completed' } : null
        );

        // Return MediaItem
        return {
          id: uploadId,
          url: uploadStatus.mediaUrl || '',
          type: file.type === 'application/pdf' ? 'pdf' : file.type.startsWith('video/') ? 'video' : 'image',
          filename: file.name,
          size: file.size,
        };
      } catch (error) {
        setUploadProgress((prev) =>
          prev
            ? {
                ...prev,
                status: 'failed',
                error: formatApiError(error).message,
              }
            : null
        );
        throw error;
      }
    },
  });

  return {
    ...mutation,
    uploadProgress,
    resetProgress: () => setUploadProgress(null),
  };
};

/**
 * Poll upload status
 */
export const useMediaStatus = (uploadId: string | null) => {
  return useQuery({
    queryKey: ['media-status', uploadId],
    queryFn: () => mediaService.getUploadStatus(uploadId!),
    enabled: !!uploadId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // Stop polling when READY (fully processed) or FAILED
      if (status === 'READY' || status === 'FAILED') {
        return false;
      }
      return 2000; // Poll every 2 seconds
    },
  });
};

/**
 * Cancel upload
 */
export const useCancelUpload = () => {
  return useMutation({
    mutationFn: (uploadId: string) => mediaService.cancelUpload(uploadId),
  });
};

// ============================================================================
// VIDEO PROCESSING HOOKS (NEW)
// ============================================================================

/**
 * Poll video processing status
 * Automatically polls while status is 'processing'
 */
export const useVideoProcessingStatus = (uploadId: string | null) => {
  return useQuery({
    queryKey: ['video-processing-status', uploadId],
    queryFn: () => mediaService.getProcessingStatus(uploadId!),
    enabled: !!uploadId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // Stop polling when READY (success) or FAILED (terminal error)
      if (status === 'READY' || status === 'FAILED') {
        return false;
      }
      // Poll every 3 seconds during processing
      return 3000;
    },
  });
};

/**
 * Retry failed video processing
 */
export const useRetryVideoProcessing = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uploadId: string) => mediaService.retryProcessing(uploadId),
    onSuccess: (_, uploadId) => {
      // Invalidate processing status to trigger re-fetch
      queryClient.invalidateQueries({
        queryKey: ['video-processing-status', uploadId],
      });
      queryClient.invalidateQueries({
        queryKey: ['media-status', uploadId],
      });
    },
  });
};

// ============================================================================
// PLATFORM REQUIREMENTS HOOKS (NEW)
// ============================================================================

/**
 * Get media requirements for all platforms
 */
export const usePlatformRequirements = () => {
  return useQuery({
    queryKey: ['platform-requirements'],
    queryFn: mediaService.getAllPlatformRequirements,
    staleTime: 60 * 60 * 1000, // 1 hour - requirements don't change often
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours
  });
};

/**
 * Get media requirements for a specific platform
 */
export const usePlatformRequirement = (platform: string | null) => {
  return useQuery({
    queryKey: ['platform-requirements', platform],
    queryFn: () => mediaService.getPlatformRequirements(platform!),
    enabled: !!platform,
    staleTime: 60 * 60 * 1000, // 1 hour
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours
  });
};
